import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { SpiderSim, SpiderState } from '../sim/Spider';
import type { Terrain } from '../game/Terrain';
import { readTune } from '../ui/TuneStore';

import { assetUrl as _assetUrl } from '../ui/AssetMap';
const MODEL_SLOT = 'wolf-spider';
const MODEL_URL_DEFAULT = '/assets/wolf-spider.glb';
const SPIDER_WORLD_LENGTH = 0.18; // metres — ~2.5× an ant, visible menace

interface Avatar {
  root: THREE.Group;
  mixer: THREE.AnimationMixer;
  walk: THREE.AnimationAction | null;
  run: THREE.AnimationAction | null;
  back: THREE.AnimationAction | null;
  active: 'walk' | 'run' | 'stopped' | 'back';
}

/** Animated wolf-spider avatars driven by the SpiderSim state. */
export class SpiderMesh {
  readonly group = new THREE.Group();
  private avatars: Avatar[] = [];
  private loaded = false;

  private readonly forward = new THREE.Vector3();
  private readonly up = new THREE.Vector3();

  constructor(private sim: SpiderSim, private terrain: Terrain) {
    this.group.name = 'spiders';
    void this.load();
  }

  private async load(): Promise<void> {
    const gltf = await new GLTFLoader().loadAsync(_assetUrl(MODEL_SLOT, MODEL_URL_DEFAULT));
    const src = gltf.scene;

    // Tuned values from the /insects tuner win if present; otherwise fall back
    // to the old auto-size path.
    const tune = readTune('wolf-spider', { scale: 0, rotX: 0, rotY: -Math.PI / 2, rotZ: 0, offY: 0 });
    if (tune.scale > 0) {
      src.rotation.set(tune.rotX, tune.rotY, tune.rotZ);
      src.scale.setScalar(tune.scale);
      src.updateMatrixWorld(true);
      const sb = new THREE.Box3().setFromObject(src);
      const c = new THREE.Vector3();
      sb.getCenter(c);
      src.position.set(-c.x, -sb.min.y + tune.offY, -c.z);
      src.updateMatrixWorld(true);
    } else {
      src.rotation.y = -Math.PI / 2;
      src.updateMatrixWorld(true);
      const bb = new THREE.Box3().setFromObject(src);
      const size = new THREE.Vector3();
      bb.getSize(size);
      const longest = Math.max(size.x, size.y, size.z) || 1;
      src.scale.setScalar(SPIDER_WORLD_LENGTH / longest);
      src.updateMatrixWorld(true);
      const bb2 = new THREE.Box3().setFromObject(src);
      const c = new THREE.Vector3();
      bb2.getCenter(c);
      src.position.set(-c.x, -bb2.min.y, -c.z);
      src.updateMatrixWorld(true);
    }

    const clips = gltf.animations;
    const walkClip = clips.find((cl) => /walking$/i.test(cl.name)) ?? clips.find((cl) => /walking(?! back)/i.test(cl.name)) ?? null;
    const runClip = clips.find((cl) => /running/i.test(cl.name)) ?? clips.find((cl) => /fast/i.test(cl.name)) ?? null;
    const backClip = clips.find((cl) => /backword|backward/i.test(cl.name)) ?? null;

    for (let i = 0; i < this.sim.spiders.length; i++) {
      const root = new THREE.Group();
      root.name = `spider-${i}`;
      root.matrixAutoUpdate = false;

      const cloned = skeletonClone(src);
      root.add(cloned);

      cloned.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.castShadow = true;
        m.receiveShadow = true;
      });

      const mixer = new THREE.AnimationMixer(cloned);
      const walk = walkClip ? mixer.clipAction(walkClip) : null;
      const run = runClip ? mixer.clipAction(runClip) : null;
      const back = backClip ? mixer.clipAction(backClip) : null;
      if (walk) {
        walk.play();
        walk.setEffectiveWeight(1);
        walk.time = Math.random() * (walkClip?.duration ?? 1);
      }
      if (run) {
        run.play();
        run.setEffectiveWeight(0);
      }
      if (back) {
        back.play();
        back.setEffectiveWeight(0);
      }

      this.avatars.push({ root, mixer, walk, run, back, active: 'walk' });
      this.group.add(root);
    }
    this.loaded = true;
  }

  update(dt: number, elapsed: number): void {
    const spiders = this.sim.spiders;
    for (let i = 0; i < spiders.length; i++) {
      const s = spiders[i];
      const av = this.avatars[i];
      if (!av) continue;

      // Dead spider — keep the corpse visible (sinking into the ground)
      // until the food pile has dropped, then hide until respawn.
      if (!s.alive) {
        if (s.foodDropped) {
          av.root.visible = false;
          continue;
        }
        av.root.visible = true;
        this.writePose(av.root, s, elapsed);
        if (av.walk) av.walk.setEffectiveWeight(0);
        if (av.run) av.run.setEffectiveWeight(0);
        if (av.back) av.back.setEffectiveWeight(0);
        continue;
      }
      av.root.visible = true;

      this.writePose(av.root, s, elapsed);

      const want: Avatar['active'] =
        s.backstepT > 0 ? 'back' :
        s.task === 'eat' ? 'stopped' :
        s.task === 'chase' ? 'run' : 'walk';
      if (want !== av.active) {
        if (av.walk) av.walk.setEffectiveWeight(want === 'walk' ? 1 : 0);
        if (av.run) av.run.setEffectiveWeight(want === 'run' ? 1 : 0);
        if (av.back) av.back.setEffectiveWeight(want === 'back' ? 1 : 0);
        av.active = want;
      }
      // When the spider is genuinely not moving (burst-state 'still'), freeze
      // the animation entirely — legs shouldn't pantomime a walk in place.
      const stationary = s.speed < 0.01 && s.task !== 'eat' && s.backstepT <= 0;
      const tempo = stationary ? 0 : 0.8 + 1.5 * Math.min(1, s.speed / 0.25);
      if (av.walk) av.walk.timeScale = tempo;
      if (av.run) av.run.timeScale = tempo;
      if (av.back) av.back.timeScale = s.backstepT > 0 ? 1.2 : 0;
      // While eating, keep walk at a slow chewing tempo instead of freezing.
      if (s.task === 'eat') {
        if (av.walk) { av.walk.setEffectiveWeight(1); av.walk.timeScale = 0.35; }
      }
      av.mixer.update(dt);
    }
    if (!this.loaded) return;
  }

  private writePose(root: THREE.Group, s: SpiderState, elapsed: number): void {
    // Small angry shake while eating — side-to-side head whip plus a vertical
    // pulse to sell "devouring". Cheap: just biases the body basis.
    const eatShake = s.task === 'eat' ? Math.sin(elapsed * 14) * 0.08 : 0;
    const eatBob   = s.task === 'eat' ? Math.abs(Math.sin(elapsed * 7)) * 0.01 : 0;
    const terrainY = this.terrain.getHeight(s.x, s.z);
    // Live spiders sit on the surface; dead ones use the sim-driven sink Y
    // (which drops them over time) plus a small immediate embed so the corpse
    // looks slumped into the dirt rather than sitting upright on top of it.
    const y = s.alive
      ? terrainY + 0.005 + eatBob
      : Math.min(s.y, terrainY) - 0.015;
    this.terrain.getNormal(s.x, s.z, this.up);
    this.forward.set(Math.cos(s.heading + eatShake), 0, Math.sin(s.heading + eatShake));
    this.forward.addScaledVector(this.up, -this.forward.dot(this.up));
    if (this.forward.lengthSq() < 1e-6) {
      this.forward.set(1, 0, 0);
      this.forward.addScaledVector(this.up, -this.forward.dot(this.up));
    }
    this.forward.normalize();

    // Dead spider: flip the body upside down by negating the up axis. Negate
    // the right basis too so the orientation stays right-handed (otherwise the
    // mesh mirrors and the shading goes inside-out).
    const upSign = s.alive ? 1 : -1;
    const upX = this.up.x * upSign;
    const upY = this.up.y * upSign;
    const upZ = this.up.z * upSign;
    const bx = (this.forward.y * upZ - this.forward.z * upY);
    const by = (this.forward.z * upX - this.forward.x * upZ);
    const bz = (this.forward.x * upY - this.forward.y * upX);

    const e = root.matrix.elements;
    e[0] = this.forward.x; e[1] = this.forward.y; e[2] = this.forward.z; e[3] = 0;
    e[4] = upX;            e[5] = upY;            e[6] = upZ;            e[7] = 0;
    e[8] = bx;             e[9] = by;             e[10] = bz;            e[11] = 0;
    e[12] = s.x;           e[13] = y;             e[14] = s.z;           e[15] = 1;
    root.matrixWorldNeedsUpdate = true;
  }
}
